import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  removeBannerSuccess,
  removeImage,
  removeImageSuccess,
  requestPersonalInfoSuccess,
  requestUser,
  requestUserImageSuccess,
  requestUserSuccess,
  updateBannerSuccess,
  updateImage,
  updateImageSuccess,
  updatePasswordSuccess,
  updateUser,
  updateUserSuccess,
} from '../actions/userActions';
import {
  CHECK_BROWSER_ONLINE,
  REMOVE_BANNER,
  REMOVE_BANNER_FAILED,
  REMOVE_IMAGE,
  REMOVE_IMAGE_FAILED,
  REQUEST_PERSONAL_INFO,
  REQUEST_PERSONAL_INFO_FAILED,
  REQUEST_USER,
  REQUEST_USER_FAILED,
  REQUEST_USER_IMAGE,
  REQUEST_USER_IMAGE_FAILED,
  SEND_PENDING_DATA,
  SET_BROWSER_ONLINE,
  UPDATE_BANNER,
  UPDATE_BANNER_FAILED,
  UPDATE_IMAGE,
  UPDATE_IMAGE_FAILED,
  UPDATE_PASSWORD,
  UPDATE_PASSWORD_FAILED,
  UPDATE_USER,
  UPDATE_USER_FAILED,
} from '../constants/index';

import safeAxios from '../../shared/api/safeFetch';
import config from '../../shared/config';
import { fetchChats } from '../actions/chatsActions';

function* requestUserHandler(action) {
  const { userId, isOwnProfile = true, full } = action.payload;
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${full ? '' : 'info/'}${userId}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    const { data: status } = yield call(safeAxios, `/api/chat/v1/users/status`, {
      body: { users: [userId] },
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });

    yield put(
      requestUserSuccess({
        data,
        isOwnProfile,
        status: status[0]?.status ?? { isOnline: false, lastSeen: null },
      })
    );
  } catch (error) {
    yield put({
      message: error.message,
      type: REQUEST_USER_FAILED,
    });
  }
}

function* updateUserHandler(action) {
  const isBrowserOnline = yield select((state) => state.user.isBrowserOnline);
  if (!isBrowserOnline) {
    yield put(
      updateUserSuccess({
        isOffline: true,
        userInfo: action.payload,
      })
    );
    return;
  }

  try {
    yield call(safeAxios, `${config.apiUrl}/user_profile/info`, {
      body: action.payload,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    yield put(updateUserSuccess());
    yield put(requestUser({ userId: action.payload.id }));
    yield put(fetchChats());
  } catch (error) {
    yield put({
      message: error.message,
      type: UPDATE_USER_FAILED,
    });
  }
}

function* checkBrowserOnline({ payload }) {
  yield put({
    payload: payload,
    type: SET_BROWSER_ONLINE,
  });
  if (payload) {
    yield put({
      type: SEND_PENDING_DATA,
    });
  }
}

function* sendPendingData() {
  const isBrowserOnline = yield select((state) => state.user.isBrowserOnline);
  if (!isBrowserOnline) {
    return;
  }
  const pendingUserInfoUpdate = yield select((state) => state.user.pendingUserInfoUpdate);
  if (pendingUserInfoUpdate) {
    const userInfo = yield select((state) => state.user.userInfo);
    yield put(updateUser(userInfo));
  }

  const pendingUserImageUpdate = yield select((state) => state.user.pendingUserImageUpdate);
  if (pendingUserImageUpdate) {
    const offlineImage = yield select((state) => state.user.offlineImage);
    const userAvatar = yield select((state) => state.user.userAvatar);
    if (offlineImage) {
      yield put(updateImage(userAvatar));
      return;
    }
    yield put(removeImage(userAvatar.userId));
  }
}

function* updateImageHandler({ payload }) {
  const isBrowserOnline = yield select((state) => state.user.isBrowserOnline);

  if (!isBrowserOnline) {
    const croppedImage = payload.croppedImage;
    delete payload.croppedImage;
    yield put(
      updateImageSuccess({
        croppedImage: croppedImage || null,
        userAvatar: payload,
      })
    );
    return;
  }

  try {
    yield call(safeAxios, `${config.apiUrl}/user_profile/info/image/${payload.userId}`, {
      body: payload.formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'PUT',
    });

    yield put(updateImageSuccess());
    yield put(requestUser({ userId: payload.userId }));
  } catch (error) {
    yield put({
      message: error.message,
      type: UPDATE_IMAGE_FAILED,
    });
  }
}

function* updateBannerHandler({ payload }) {
  const isBrowserOnline = yield select((state) => state.user.isBrowserOnline);
  if (!isBrowserOnline) {
    yield put(
      updateBannerSuccess({
        bannerImage: payload,
      })
    );
    return;
  }
  try {
    yield call(safeAxios, `${config.apiUrl}/user_profile/info/banner/${payload.userId}`, {
      body: payload.formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'PUT',
    });

    yield put(updateBannerSuccess());
    yield put(requestUser({ userId: payload.userId }));
  } catch (error) {
    yield put({
      message: error.message,
      type: UPDATE_BANNER_FAILED,
    });
  }
}

function* removeImageHandler(action) {
  try {
    yield call(safeAxios, `${config.apiUrl}/user_profile/info/image/${action.payload}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });

    yield put(removeImageSuccess());
  } catch (error) {
    yield put({
      message: error.message,
      type: REMOVE_IMAGE_FAILED,
    });
  }
}

function* removeBannerHandler(action) {
  try {
    yield call(safeAxios, `${config.apiUrl}/user_profile/info/banner/${action.payload}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });

    yield put(removeBannerSuccess());
  } catch (error) {
    yield put({
      message: error.message,
      type: REMOVE_BANNER_FAILED,
    });
  }
}

function* updatePasswordHandler(action) {
  try {
    yield call(safeAxios, `${config.authUrl}/oauth2c/change-password`, {
      body: action.payload.data,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    yield put(updatePasswordSuccess());
  } catch (error) {
    console.error(error, 'error');
    const message = `${error.message}`.includes('Failed to fetch') ? '401' : error.message;
    yield put({
      message: message,
      type: UPDATE_PASSWORD_FAILED,
    });
  }
}

function* requestPersonalInfoHandler() {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user/me`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      method: 'GET',
    });

    yield put(requestPersonalInfoSuccess(data));
  } catch (error) {
    yield put({
      message: error.message,
      type: REQUEST_PERSONAL_INFO_FAILED,
    });
  }
}

function* requestUserImageHandler(action) {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}${action.payload?.path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      method: 'GET',
      responseType: 'arraybuffer',
    });
    yield put(requestUserImageSuccess(data));
  } catch (error) {
    yield put({
      message: error.message,
      type: REQUEST_USER_IMAGE_FAILED,
    });
  }
}

export default function* fetchUserWatcher() {
  yield takeEvery(REQUEST_USER, requestUserHandler);
  yield takeEvery(UPDATE_USER, updateUserHandler);
  yield takeEvery(UPDATE_IMAGE, updateImageHandler);
  yield takeEvery(UPDATE_BANNER, updateBannerHandler);
  yield takeEvery(REMOVE_IMAGE, removeImageHandler);
  yield takeEvery(REMOVE_BANNER, removeBannerHandler);
  yield takeEvery(UPDATE_PASSWORD, updatePasswordHandler);
  yield takeEvery(REQUEST_PERSONAL_INFO, requestPersonalInfoHandler);
  yield takeEvery(REQUEST_USER_IMAGE, requestUserImageHandler);
  yield takeEvery(SEND_PENDING_DATA, sendPendingData);
  yield takeEvery(CHECK_BROWSER_ONLINE, checkBrowserOnline);
}
